import clsx from 'clsx'
import { HugeiconsIcon } from '@hugeicons/react'
import { Trans, useLingui } from '@lingui/react/macro'

import {
  SquareLock01Icon,
  Tick01Icon,
  Cancel01Icon
} from '@hugeicons/core-solid-rounded'

import * as Form from '@/features/form'
import Input from '@/components/ui/Input'

type FormData = {
  password: string
  confirmPassword: string
}

export function PasswordField() {
  const { t } = useLingui()

  return (
    <>
      <Form.Field<FormData, 'password'>
        name="password"
        label={t`Password`}
        rules={{
          validate: {
            length(password: string) {
              if (password.length < 8) return false
            },
            hasNumber(password: string) {
              if (!password.match(/[0-9]/)) return false
            },
            hasCapital(password: string) {
              if (!password.match(/[A-Z]/)) return false
            },
            required: Form.Validations.requiredWithTrimValidation(
              t`This field is required`
            )
          }
        }}
      >
        {({ trigger, renderProps: { onChange, value, ...props } }) => {
          const passwordLongEnough = value && value.length >= 8
          const passwordHasNumber = value && value.match(/[0-9]/)
          const passwordhasCapital = value && value.match(/[A-Z]/)

          return (
            <>
              <Input
                type="password"
                value={value}
                Icon={SquareLock01Icon}
                onChange={event => {
                  onChange(event.target.value)
                  trigger(['confirmPassword'])
                }}
                placeholder=""
                autoComplete="new-password webauthn"
                {...props}
              />
              <div className="flex flex-col gap-1 p-2 pl-3'">
                <span
                  className={clsx(
                    'flex flex-row gap-1 items-center',
                    passwordLongEnough ? 'text-green-500' : 'text-red-500'
                  )}
                >
                  <HugeiconsIcon
                    icon={passwordLongEnough ? Tick01Icon : Cancel01Icon}
                    className="size-4"
                  />
                  <span>
                    <Trans>At least 8 characters</Trans>
                  </span>
                </span>
                <span
                  className={clsx(
                    'flex flex-row gap-1 items-center',
                    passwordHasNumber ? 'text-green-500' : 'text-red-500'
                  )}
                >
                  <HugeiconsIcon
                    icon={passwordHasNumber ? Tick01Icon : Cancel01Icon}
                    className="size-4"
                  />
                  <span>
                    <Trans>At least 1 number</Trans>
                  </span>
                </span>
                <span
                  className={clsx(
                    'flex flex-row gap-1 items-center',
                    passwordhasCapital ? 'text-green-500' : 'text-red-500'
                  )}
                >
                  <HugeiconsIcon
                    icon={passwordhasCapital ? Tick01Icon : Cancel01Icon}
                    className="size-4"
                  />
                  <span>
                    <Trans>At least 1 capital letter</Trans>
                  </span>
                </span>
              </div>
            </>
          )
        }}
      </Form.Field>
      <Form.Field<FormData, 'confirmPassword'>
        name="confirmPassword"
        label={t`Confirm password`}
        rules={{
          validate: {
            compare(password, data) {
              if (!data.password) return

              if (password !== data.password) {
                return t`Passwords have to match.`
              }
            }
          }
        }}
      >
        {({ trigger, renderProps: { onChange, ...props } }) => (
          <Input
            type="password"
            Icon={SquareLock01Icon}
            onChange={event => {
              onChange(event.target.value)
              trigger(['confirmPassword'])
            }}
            placeholder=""
            autoComplete="new-password webauthn"
            {...props}
          />
        )}
      </Form.Field>
    </>
  )
}
